import * as React from "react";

import { Dispatch } from "redux";
import { connect } from "react-redux";
import { ReduxActions, ReduxState } from "../../store";

import { v4 as uuidv4 } from "uuid";
import deepEqual from "fast-deep-equal";
import LocalizedInput from "./localized-input";
import strings from "../../localization/strings";
import styles from "../../styles/generic/form-item";
import { SnackbarMessage } from "../layouts/app-layout";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SimpleReactValidator from "simple-react-validator";
import { Place, PlacePositionTypeEnum } from "../../generated/client";
import CoordinatePicker from "../admin/admin-places/coordinate-picker";
import { CustomStyles, Locale, Localizations, LocalizedValues, NullableToken } from "../../types";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography, withStyles, WithStyles } from '@material-ui/core';
import theme from "../../dynamic-content/theme";

/**
 * Interface describing component properties
 */
interface Props extends WithStyles<typeof styles> {
  places: Place[];
  showMessages: boolean;
  eventLocation?: Place;
  placesLoaded?: boolean;
  accessToken?: NullableToken;
  customStyles?: CustomStyles;
  localizations: Localizations;
  validator: SimpleReactValidator;
  placeDescription: LocalizedValues;
  onPlaceUpdate: (place: Place) => void;
  setSnackbarMessage?: (message: SnackbarMessage) => void;
  onPlaceDescriptionUpdate: (locale: string, value: string) => void;
}

/**
 * Interface describing component state
 */
interface State {
  isMobile: boolean;
  newPlace: Place;
  tabIndex: number;
  disabled: boolean;
  division: string | undefined;
  address_localities: string[];
}

/**
 * Event location component
 */
class EventLocation extends React.Component<Props, State> {

  /**
   * Constructor
   *
   * @param props properties
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      isMobile: this.isMobileScreen(),
      tabIndex: 0,
      disabled: false,
      division:  this.props.eventLocation?.addressLocality?.fi || "Alajärvi",
      newPlace: {
        name: {
          fi: "",
          sv: "",
          en: ""
        },
        position: {
          coordinates: [
            62.7880085,
            22.8492787
          ],
          type: PlacePositionTypeEnum.Point
        },
        customData: {
          listed: "false",
        },
        streetAddress: {
          fi: "",
          sv: "",
          en: ""
        },
        addressLocality: {
          fi: "",
          sv: "",
          en: ""
        },
        dataSource: process.env.REACT_APP_DATASOURCE
      },
      address_localities: [
        "Alajärvi",
        "Alavus",
        "Evijärvi",
        "Ilmajoki",
        "Isojoki",
        "Isokyrö",
        "Karijoki",
        "Kauhajoki",
        "Kauhava",
        "Kuortane",
        "Kurikka",
        "Lappajärvi",
        "Lapua",
        "Seinäjoki",
        "Soini",
        "Teuva",
        "Vimpeli",
        "Ähtäri"
      ]
    };
  }

  /**
   * Component did mount life cycle method
   */
  public componentDidMount = () => {
    window.addEventListener("resize", this.handleResize);
  }

  /**
   * Component did update
   *
   * @param prevProps previous component properties
   * @param prevState previous component state
   */
  public componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (!deepEqual(prevState.newPlace, this.state.newPlace)) {
      this.setState({
        disabled: false
      });
    }
  }

  /**
   * Component will unmount life cycle method
   */
  public componentWillUnmount = () => {
    window.removeEventListener("resize", this.handleResize);
  }

  /**
   * Component render
   */
  public render = () => {
    const { classes } = this.props;
    const { newPlace, tabIndex } = this.state;

    if (!newPlace.addressLocality) {
      return null;
    }

    return (
      <Box mt={ 2 } mb={ 2 }>
        <Tabs
          onChange={ this.setTabIndex }
          value={ tabIndex }
        >
          <Tab value={ 0 } label={ strings.eventForm.chooseExistingPlace } />
          <Tab value={ 1 } label={ strings.eventForm.addNewPlace } />
        </Tabs>
        <Box className={ classes.content }>
          { this.renderExistingPlaceForm(tabIndex === 0) }
          { this.renderNewPlaceForm(tabIndex === 1) }
        </Box>
      </Box>
    );
  }

  /**
   * Add existing place render method
   *
   * @param visible boolean
   */
  private renderExistingPlaceForm = (visible: boolean) => {
    const {
      places,
      classes,
      validator,
      placesLoaded,
      eventLocation,
      localizations,
      placeDescription,
      onPlaceDescriptionUpdate
    } = this.props;

    const { address_localities, division } = this.state;

    return (
      <Box display={ visible ? "block" : "none" } mt={ 2 } >
        <FormControl fullWidth className={ classes.spacing }>
          <InputLabel id="label">
            { strings.eventForm.cityOrMunicipality }
          </InputLabel>
          <Select
            fullWidth
            labelId="label"
            value={ division && address_localities.includes(division) ? division : undefined }
            onChange={ this.updateNewPlaceOrDivision }
          >
            {
              address_localities.map((address_locality, index) =>
                <MenuItem key={ index } value={ address_locality }>
                  { address_locality }
                </MenuItem>
              )
            }
          </Select>
          {this.renderValidatorMessage("division", division)}
        </FormControl>
        <div className={ classes.spacing }>
          { placesLoaded &&
            <Autocomplete
              id="event-location"
              value={ eventLocation ? eventLocation : null }
              getOptionSelected={ (option, value) => option.id === value.id }
              getOptionLabel={ (place) => place.name.fi ? place.name.fi : "" }
              options={ places.filter(place => place.addressLocality?.fi === division) }
              renderInput={(params) =>
                <TextField
                  { ...params }
                  label={ strings.eventForm.location }
                />
              }
              onChange={ this.updatePlace }
            />
          }
          { this.renderValidatorMessage("location", eventLocation?.id || eventLocation?.name.fi) }
        </div>
        <div className={ classes.spacing }>
          <LocalizedInput
            validator={ validator }
            localizations={ localizations }
            onChange={ onPlaceDescriptionUpdate }
            localizedStrings={ strings.eventForm.placeDescription }
            localizedValues={ (placeDescription || { fi: "", sv: "", en: "" }) as LocalizedValues }
          />
        </div>
      </Box>
    );
  }

  /**
   * Add new place render method
   *
   * @param visible boolean
   */
  private renderNewPlaceForm = (visible: boolean) => {
    const {
      classes,
      localizations,
      eventLocation,
    } = this.props;
    const { isMobile, newPlace, disabled } = this.state;

    if (!newPlace.addressLocality) {
      return null;
    }

    return (
      <Box display={ visible ? "block" : "none" } mt={ 3 } className={ classes.newPlaceForm }>
        <Box mb={ 3 }>
        <Typography variant="h5" style={{ marginTop: 8 }}>
          {
            strings.formatString(
              strings.eventForm.eventNewLocationDescription,
              isMobile ?
                strings.eventForm.eventNewLocationDescriptionBelow :
                strings.eventForm.eventNewLocationDescriptionSide
            )
          }
        </Typography>
        <CoordinatePicker
          place={ newPlace }
          onUpdateLocation={ this.setNewPlaceLocation }
        />
        </Box>
        <Box mb={ 3 }>
          { localizations.fi &&
            <TextField
              fullWidth
              disabled={ disabled }
              value={ newPlace.name.fi }
              onChange={ this.setNewPlaceName("fi") }
              label={ strings.eventForm.newPlaceName.fi }
            />
          }
          { localizations.en &&
            <TextField
              fullWidth
              disabled={ disabled }
              value={ newPlace.name.en }
              onChange={ this.setNewPlaceName("en") }
              label={ strings.eventForm.newPlaceName.en }
            />
          }
        </Box>
        { this.renderValidatorMessage("new_location", eventLocation?.id || eventLocation?.name.fi) }
        <Button disabled={ disabled } onClick={ this.updateNewPlaceOrDivision }>
          { disabled ?
            strings.eventForm.newPlaceAdded :
            strings.eventForm.addNewPlaceButtonText
          }
        </Button>
      </Box>
    );
  }

  /**
   * Method for setting autocomplete options
   *
   * @param updatedPlace linked events place object
   */
  private setNewPlaceLocation = (updatedPlace: Place) => {
    let newDivison = this.state.division;
    
    const locality = updatedPlace.addressLocality?.fi;
    if (locality && this.state.address_localities.includes(locality)) {
      newDivison = locality;
    }
    if (locality && !this.state.address_localities.includes(locality)) {
      const addressLocality = this.state.newPlace.addressLocality?.fi ? this.state.newPlace.addressLocality?.fi : "Alajärvi";
      updatedPlace.addressLocality = { "fi": addressLocality, "sv": addressLocality, "en": addressLocality };
      updatedPlace.addressRegion = addressLocality;
    } 

    this.setState({ newPlace: updatedPlace, division: newDivison });
  }

  /**
   * Sets tab index
   *
   * @param event event object
   * @param newValue new tab index value
   */
  private setTabIndex = (event: React.ChangeEvent<{}>, newValue: number) => {
    this.setState({ tabIndex: newValue });
  }

  /**
   * Method for rendering validator message
   *
   * @param field field
   * @param value value
   */
  private renderValidatorMessage = (field: string, value?: string) => {
    const { validator } = this.props;
    return validator.message(field, value, "required");
  }

  /**
   * Event handler for localized field change
   *
   * @param property property
   * @param locale locale
   * @param value value
   */
  private setNewPlaceName = (locale: Locale) => (event: React.ChangeEvent<any>) => {
    const { newPlace } = this.state;
    const updatedPlace: Place = { ...newPlace };
    updatedPlace["name"] = updatedPlace["name"] || {};
    updatedPlace["name"][locale] = event.target.value;
    this.setState({
      newPlace: updatedPlace
    });
  }

  /**
   * Method for updating event's place
   *
   * @param event React change event
   * @param value new place
   */
  private updatePlace = (event: React.ChangeEvent<any>, value: Place | null) =>  {
    const { onPlaceUpdate } = this.props;

    if (!value) {
      return;
    }

    onPlaceUpdate(value);
  }

  /**
   * Method for updating new place. either when division is updated or on button click.
   *
   * @param event event used, when method is called by division update
   */
  private updateNewPlaceOrDivision = async (event: React.ChangeEvent<any>) => {
    const { onPlaceUpdate, setSnackbarMessage, localizations } = this.props;
    const { newPlace, address_localities } = this.state;
    const { value } = event.target;
    const updatedPlace = { ...newPlace };

    if (!setSnackbarMessage) {
      return;
    }

    if(!value) {
      if (localizations.en) {
        if (!updatedPlace.name.en || !updatedPlace.name.fi || !updatedPlace.addressRegion) {
          setSnackbarMessage({
            severity: "warning",
            message: strings.place.placeMissingFieldsWarning
          });

          return;
        }
      }

      if (!updatedPlace.name.fi || !updatedPlace.addressRegion) {
        setSnackbarMessage({
          severity: "warning",
          message: strings.place.placeMissingFieldsWarning
        });

        return;
      }
    }

    if (!updatedPlace.originId) {
      const originId = this.generateOriginId();
      updatedPlace.originId = originId;
    }
    if (value !== undefined && updatedPlace.addressLocality) {
      if (!address_localities.includes(value)) return;
      updatedPlace.addressLocality = { "fi": value, "sv": value, "en": value };
      updatedPlace.addressRegion = value;
      this.setState({ division: address_localities.includes(value) ? value : undefined });
    }
    onPlaceUpdate(updatedPlace);

    this.setState({
      newPlace: updatedPlace,
      disabled: true,
      tabIndex: 0
    });
  }

  /**
   * Method for generating origin id
   */
  private generateOriginId = (): string => {
    const { newPlace } = this.state;
    return `${newPlace.dataSource}:${uuidv4()}`;
  }

  /**
   * Event handler for window resizing
   */
  private handleResize = () => {
    this.setState({ isMobile: this.isMobileScreen() });
  }

  /**
   * Returns whether window is mobile sized or not
   */
  private isMobileScreen = () => {
    return window.innerWidth < theme.breakpoints.values.md;
  }
}

/**
 * Redux mapper for mapping store state to component props
 *
 * @param state store state
 */
const mapStateToProps = (state: ReduxState) => ({
  accessToken: state.auth.accessToken,
  locale: state.locale.locale
});

/**
 * Redux mapper for mapping component dispatches
 *
 * @param dispatch dispatch method
 */
const mapDispatchToProps = (dispatch: Dispatch<ReduxActions>) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EventLocation));